// Dual-engine pivot support: dimension axis split, client-side level computation
// and the shared pivot model consumed by <PivotTable/> for both engines.
import { smartComparator } from "@/lib/filterValues";
import { dimOutNames } from "@/lib/dimKeys";
export const PSEP = "\u001f";

export function dimOutName(d) {
  if (!d) return null;
  if (d.key) return d.key;
  if (d.alias) return d.alias;
  if (d.date_part && d.date_part !== "none") return `${d.field}_${d.date_part}`;
  return d.field;
}

// Split config.dimensions into row/column dims. Legacy pivots (no axis flags): last dim = column.
export function splitPivotDimObjects(config) {
  const dims = (config.dimensions || []).filter((d) => d && (d.key || d.field));
  const hasAxis = dims.some((d) => d.axis === "col" || d.axis === "row");
  if (!hasAxis && dims.length >= 2) return { rowObjs: dims.slice(0, -1), colObjs: dims.slice(-1) };
  return { rowObjs: dims.filter((d) => d.axis !== "col"), colObjs: dims.filter((d) => d.axis === "col") };
}

export function splitPivotDims(config, columns = []) {
  const { rowObjs, colObjs } = splitPivotDimObjects(config);
  // Item 35: instance-aware names so one date field can drive two pivot levels
  const names = dimOutNames([...rowObjs, ...colObjs]);
  const pick = (arr, offset) => arr.map((_, i) => names[offset + i]).filter((n) => n && columns.includes(n));
  return { rowDims: pick(rowObjs, 0), colDims: pick(colObjs, rowObjs.length) };
}

function aggVals(vals, agg) {
  const nums = vals.filter((v) => Number.isFinite(v));
  if (!nums.length) return null;
  if (agg === "avg") return nums.reduce((a, b) => a + b, 0) / nums.length;
  if (agg === "min") return Math.min(...nums);
  if (agg === "max") return Math.max(...nums);
  return nums.reduce((a, b) => a + b, 0);
}

// Client-side engine: re-aggregate the base result at every (rowDepth, colDepth) combination.
// Subtotals of avg/median are approximations (aggregated from aggregates); the server engine is exact.
export function computeClientLevels(baseRows, rowDims, colDims, measureKeys, aggByKey = {}) {
  const R = rowDims.length, C = colDims.length;
  const levels = [];
  for (let i = R; i >= 0; i--) {
    for (let j = C; j >= 0; j--) {
      if (i === R && j === C) { levels.push({ r: i, c: j, rows: baseRows }); continue; }
      const dims = [...rowDims.slice(0, i), ...colDims.slice(0, j)];
      const groups = new Map();
      (baseRows || []).forEach((row) => {
        const k = dims.map((d) => String(row[d] ?? "")).join(PSEP);
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(row);
      });
      const rows = [];
      groups.forEach((grp) => {
        const rec = {};
        dims.forEach((d) => { rec[d] = grp[0][d]; });
        measureKeys.forEach((m) => { rec[m] = aggVals(grp.map((r) => Number(r[m])), aggByKey[m] || "sum"); });
        rows.push(rec);
      });
      levels.push({ r: i, c: j, rows });
    }
  }
  return levels;
}

const pathOf = (rec, dims, depth) => dims.slice(0, depth).map((d) => String(rec[d] ?? "\u2014")).join(PSEP);

export function buildPivotModel({ levels, rowDims, colDims, measureKeys, sortBy = "label", sortDir = "asc", sortMeasure = null, levelSort = null, colLevelSort = null }) {
  const R = rowDims.length, C = colDims.length;
  const lookup = new Map();
  (levels || []).forEach(({ r, c, rows }) => {
    (rows || []).forEach((rec) => {
      lookup.set(`${r}|${c}|${pathOf(rec, rowDims, r)}|${pathOf(rec, colDims, c)}`, rec);
    });
  });
  const getCell = (rPath, cPath, m) => {
    const rec = lookup.get(`${rPath.length}|${cPath.length}|${rPath.join(PSEP)}|${cPath.join(PSEP)}`);
    if (!rec) return null;
    const v = rec[m];
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const base = (levels.find((l) => l.r === R && l.c === C) || {}).rows || [];
  const m0 = sortMeasure && measureKeys.includes(sortMeasure) ? sortMeasure : measureKeys[0];
  let colTuples = [[]];
  if (C > 0) {
    const seen = new Map();
    base.forEach((rec) => {
      const t = colDims.map((d) => String(rec[d] ?? "\u2014"));
      seen.set(t.join(PSEP), t);
    });
    // Item 27: months / quarters / weekdays sort chronologically at every column level
    // Item 6: each column level can also sort ascending / descending / by value / manually.
    const segCmp = colDims.map((_, i) => smartComparator([...seen.values()].map((t) => t[i])));
    colTuples = [...seen.values()].sort((a, b) => {
      for (let i = 0; i < a.length; i++) {
        const cs = (colLevelSort && colLevelSort[i]) || {};
        const dir = cs.dir === "desc" ? -1 : 1;
        let r;
        if (cs.by === "manual" && Array.isArray(cs.manual) && cs.manual.length) {
          const order = new Map(cs.manual.map((v, k) => [String(v).trim().toLowerCase(), k]));
          const ka = String(a[i]).trim().toLowerCase(), kb = String(b[i]).trim().toLowerCase();
          const ia = order.has(ka) ? order.get(ka) : 1e9;
          const ib = order.has(kb) ? order.get(kb) : 1e9;
          r = ia !== ib ? ia - ib : segCmp[i](a[i], b[i]);
        } else if (cs.by === "value" && m0) {
          r = (getCell([], a.slice(0, i + 1), m0) ?? -Infinity) - (getCell([], b.slice(0, i + 1), m0) ?? -Infinity);
        } else {
          r = segCmp[i](a[i], b[i]);
        }
        if (r) return r * dir;
      }
      return 0;
    });
  }

  // Item 84: independent sorting at every hierarchy level (label / value / manual)
  // "inherit" (or no setting) falls back to the pivot-level default without touching siblings.
  const cmpFor = (depth, labels = []) => {
    const ls = levelSort && levelSort[depth - 1];
    const by = (ls?.by && ls.by !== "inherit") ? ls.by : sortBy;
    const dir = (ls?.by && ls.by !== "inherit" ? ls?.dir : null) || sortDir;
    const m = ls?.measure && measureKeys.includes(ls.measure) ? ls.measure : m0;
    const labelCmp = smartComparator(labels);
    if (by === "manual" && Array.isArray(ls?.manual) && ls.manual.length) {
      const order = new Map(ls.manual.map((v, i) => [String(v).trim().toLowerCase(), i]));
      // Values not listed keep the level's default order (inherited label/value sort).
      const fallback = (a, b) => {
        let r;
        if ((sortBy || "label") === "value") r = (getCell(a.path, [], m0) ?? -Infinity) - (getCell(b.path, [], m0) ?? -Infinity);
        else r = labelCmp(a.value, b.value);
        return (sortDir === "desc" ? -r : r);
      };
      const idx = (n) => {
        const k = String(n.value ?? "").trim().toLowerCase();
        return order.has(k) ? order.get(k) : 1e9;
      };
      return (a, b) => {
        const ia = idx(a), ib = idx(b);
        if (ia !== ib) return ia - ib;
        return fallback(a, b);
      };
    }
    return (a, b) => {
      let r;
      if (by === "value") r = (getCell(a.path, [], m) ?? -Infinity) - (getCell(b.path, [], m) ?? -Infinity);
      else r = labelCmp(a.value, b.value);
      return dir === "desc" ? -r : r;
    };
  };
  const groupKeys = [];
  const buildNodes = (prefix) => {
    const depth = prefix.length + 1;
    const lvl = levels.find((l) => l.r === depth && l.c === 0);
    const seen = new Map();
    (lvl?.rows || []).forEach((rec) => {
      const p = rowDims.slice(0, depth).map((d) => String(rec[d] ?? "\u2014"));
      if (prefix.every((v, k) => p[k] === v)) seen.set(p[depth - 1], p);
    });
    const nodes = [...seen.entries()].map(([value, p]) => ({ value, path: p, children: [] }));
    nodes.sort(cmpFor(depth, nodes.map((n) => n.value)));
    nodes.forEach((n) => {
      if (depth < R) { groupKeys.push(n.path.join(PSEP)); n.children = buildNodes(n.path); }
    });
    return nodes;
  };
  const rowTree = R > 0 ? buildNodes([]) : [];
  return { getCell, colTuples, rowTree, groupKeys, R, C };
}
